import moment from 'moment';
import { DebtType, NegotiationType } from '../../generated/graphql';
import { getEntityLabel } from '../../utils/financialEntitiesList';
import { resolveBrandCountry } from '../../branding/common/utils/resolveBrandCountry';
import { getBrandLabel } from '../../utils/getBrandLabel';
import { getDebtTypeLabel } from '../../utils/getDebtTypeLabel';
import { getNegotiationOptionLabel } from '../../utils/getNegotiationOptionLabel';
import thousandSeparator from '../../utils/thousandSeparator';
import { DictionaryContextValueType } from '../context/DictionaryContextTypes';

// Normalize all values to work as strings
const n = (value: DictionaryContextValueType) => {
  return `${value}`;
};

const getMoment = (date: DictionaryContextValueType) => {
  if (typeof date === 'boolean') {
    return moment();
  }
  return moment(date);
};

const percent = (value: DictionaryContextValueType) => `${(parseFloat(n(value)) * 100).toFixed(2)}%`;
const money = (value: DictionaryContextValueType) => n(`$${thousandSeparator(n(value))}`);
const number = (value: DictionaryContextValueType) => n(thousandSeparator(parseInt(n(value), 10)));
const shortDate = (date: DictionaryContextValueType) => n(getMoment(date).format('D MMM YYYY'));
const longDate = (date: DictionaryContextValueType) => n(getMoment(date).format('DD [de] MMMM [de] YYYY'));
const lastDigits = (value: DictionaryContextValueType) => n(value).slice(-4) || '';
const lastDigitsPh = (value: DictionaryContextValueType) => `&ast;&ast;&ast;&ast; ${lastDigits(value)}`;
const truncatedEmail = (value: DictionaryContextValueType) => {
  const [ left, right ] = n(value).toLowerCase().split('@');
  return [ left[0], ...Array.from({ length: left.length - 1 }).fill('*'), '@', right ].join('');
};
const truncatedPhone = (value: DictionaryContextValueType) => `*** *** ${n(value).slice(-4)}` || '';
const bank = (value: DictionaryContextValueType) => {
  const { brand: currentBrand } = resolveBrandCountry();
  return getEntityLabel(n(value), currentBrand) || n(value) || `${value}` || '';
};
const brand = (value: DictionaryContextValueType) => getBrandLabel(n(value)) || n(value) || `${value}` || '';
const debtTypeLabel = (value: DictionaryContextValueType) => getDebtTypeLabel(n(value) as DebtType);
const negTypeLabel = (value: DictionaryContextValueType) => getNegotiationOptionLabel(
  n(value) as NegotiationType,
);
const name = (value: DictionaryContextValueType) => n(value).toLowerCase().split(' ').map((item) => item[0].toUpperCase() + item.slice(1))
  .join(' ') || '';

const pipes: { [key: string]: (value: DictionaryContextValueType) => string } = {
  money,
  number,
  longDate,
  shortDate,
  lastDigits,
  lastDigitsPh,
  truncatedEmail,
  truncatedPhone,
  bank,
  debtTypeLabel,
  name,
  negTypeLabel,
  brand,
  percent,
};

const compose = (fns: string[]) => (x: DictionaryContextValueType) => fns.reduce(
  (v: DictionaryContextValueType, f: string) => {
    if (!pipes[f]) return v;
    return pipes[f](v);
  }, x,
);

export { compose };
